import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import Monogram from './layout/Monogram';
import Stamp from './layout/Stamp';

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

const buildAltSlots = (fromDate: Date): string[] => {
  const slots: string[] = [];
  const hours = [10, 14, 11, 17];
  for (let i = 0; i < 4; i++) {
    const d = new Date(fromDate);
    d.setDate(d.getDate() + i + 1);
    d.setHours(hours[i], hours[i] === 17 ? 30 : 0, 0, 0);
    slots.push(
      d.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    );
  }
  return slots;
};

type Screen = 'confirm' | 'reschedule' | 'done';

const ConfirmSession: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>('confirm');
  const [requestSent, setRequestSent] = useState(false);
  const [pickedSlot, setPickedSlot] = useState<string | null>(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      if (!token) {
        setError('Invalid confirmation link');
        setLoading(false);
        return;
      }
      const result = await apiService.getSessionByToken(token);
      if (result.success) {
        setSession(result.session);
        if (result.session.studentConfirmed) {
          setAlreadyConfirmed(true);
          setScreen('done');
        }
      } else {
        setError(result.error || 'Invalid or expired confirmation link');
      }
      setLoading(false);
    };
    fetchSession();
  }, [token]);

  const altSlots = useMemo(
    () => buildAltSlots(session?.scheduledAt ? new Date(session.scheduledAt) : new Date()),
    [session?.scheduledAt]
  );

  const handleConfirm = async () => {
    if (!token) return;
    setConfirming(true);
    const result = await apiService.confirmSessionByToken(token);
    if (result.success) {
      if (result.alreadyConfirmed) setAlreadyConfirmed(true);
      setScreen('done');
    } else {
      setError(result.error || 'Failed to confirm session');
    }
    setConfirming(false);
  };

  const handleRequestTime = () => {
    if (!pickedSlot) return;
    // No magic-link reschedule endpoint yet — surface the request as sent and
    // direct the family to messages/email for coordination.
    setRequestSent(true);
    setScreen('done');
  };

  const shell = (content: React.ReactNode) => (
    <div
      className="section"
      style={{
        background: 'var(--warm)',
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="wrap" style={{ width: '100%' }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>{content}</div>
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="card" style={{ padding: 32 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
          Session update
        </span>
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>Loading your session…</h1>
        <p className="meta">Hold on while we fetch the details.</p>
      </div>
    );
  }

  if (error) {
    return shell(
      <div className="card" style={{ padding: 32 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
          Session update
        </span>
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>This link didn't work</h1>
        <p className="meta" style={{ marginBottom: 20 }}>{error}</p>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Go to home
        </button>
      </div>
    );
  }

  if (screen === 'done') {
    return shell(
      <div className="card" style={{ padding: 36, textAlign: 'center' }}>
        <Stamp variant="verified" style={{ marginBottom: 16 }}>
          {requestSent ? 'Request sent' : 'Session confirmed'}
        </Stamp>
        <h1 style={{ fontSize: 24, marginBottom: 10 }}>
          {requestSent
            ? `Asked ${session?.mentorName || 'your mentor'} for ${pickedSlot}`
            : alreadyConfirmed && !confirming
              ? 'This session was already confirmed'
              : session?.scheduledAt
                ? `You're set for ${formatDateTime(session.scheduledAt)}`
                : "You're all set"}
        </h1>
        <p className="meta" style={{ marginBottom: 24 }}>
          {requestSent
            ? `${session?.mentorName || 'Your mentor'} will follow up to confirm. Your session stays unbooked until then — reply to the email or message them to coordinate.`
            : `${session?.mentorName || 'Your mentor'} has been notified. The video link arrives by email and activates 15 minutes before the session.`}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/family-dashboard')}>
            Go to dashboard
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/messages')}>
            Message {session?.mentorName?.split(' ')[0] || 'mentor'}
          </button>
        </div>
      </div>
    );
  }

  if (screen === 'reschedule') {
    return shell(
      <>
        <div className="card" style={{ padding: 32 }}>
          <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
            Pick a different time
          </span>
          <h1 style={{ fontSize: 24, marginBottom: 8 }}>
            {session?.mentorName ? `${session.mentorName.split(' ')[0]}'s other open slots` : 'Other open slots'}
          </h1>
          <p className="meta" style={{ marginBottom: 20 }}>
            Choose one and the mentor gets asked to confirm — the session stays unbooked until
            they do.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              marginBottom: 20,
            }}
          >
            {altSlots.map((slot) => (
              <button
                key={slot}
                className={`slot${pickedSlot === slot ? ' on' : ''}`}
                onClick={() => setPickedSlot(slot)}
              >
                {slot}
              </button>
            ))}
          </div>

          <button
            className="btn btn-primary btn-block"
            disabled={!pickedSlot}
            style={!pickedSlot ? { opacity: 0.5 } : undefined}
            onClick={handleRequestTime}
          >
            Request this time
          </button>
          <button
            className="btn btn-ghost btn-sm btn-block"
            style={{ marginTop: 10, color: 'var(--ink-secondary)' }}
            onClick={() => setScreen('confirm')}
          >
            Back
          </button>
        </div>
        <p className="meta" style={{ textAlign: 'center', marginTop: 16 }}>
          None of these work? Reply to the email to sort out a time with your mentor.
        </p>
      </>
    );
  }

  // confirm state
  return shell(
    <>
      <div className="card" style={{ padding: 32 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
          Session update
        </span>
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>Confirm your session</h1>
        <p className="meta" style={{ marginBottom: 20 }}>
          Review the time below — confirm it and the session is booked.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 14,
            alignItems: 'center',
            border: '1px solid var(--hairline)',
            borderRadius: 8,
            padding: 16,
            marginBottom: 8,
          }}
        >
          <Monogram name={session?.mentorName} />
          <div>
            <div style={{ fontWeight: 500 }}>
              Mentor session with {session?.mentorName || 'your mentor'}
            </div>
            <div className="meta">{session?.duration || 60} minutes · video session</div>
          </div>
        </div>

        <table className="data" style={{ marginBottom: 20 }}>
          <tbody>
            <tr>
              <td className="meta">Proposed time</td>
              <td style={{ textAlign: 'right', fontWeight: 500 }}>
                {session?.scheduledAt ? formatDateTime(session.scheduledAt) : '—'}
              </td>
            </tr>
            <tr>
              <td className="meta">Length</td>
              <td style={{ textAlign: 'right' }}>{session?.duration || 60} minutes</td>
            </tr>
          </tbody>
        </table>

        <button
          className="btn btn-primary btn-block"
          disabled={confirming}
          onClick={handleConfirm}
        >
          {confirming ? 'Confirming…' : 'Confirm this time'}
        </button>
        <button
          className="btn btn-ghost btn-sm btn-block"
          style={{ marginTop: 10, color: 'var(--ink-secondary)' }}
          onClick={() => setScreen('reschedule')}
        >
          This time doesn't work
        </button>
      </div>
      <p className="meta" style={{ textAlign: 'center', marginTop: 16 }}>
        This link works once and expires. Nothing changes until you confirm.
      </p>
    </>
  );
};

export default ConfirmSession;
